import AddCircleOutlined from "@mui/icons-material/AddCircleOutlined";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import { useToken } from "../../server/auth/use-token";
import { ImagesService } from "../../server/cloudflare/images-service";
import { uuidv4 } from "../../utils/uuid";
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_REFERENCE_IMAGES,
  MAX_REFERENCE_IMAGE_SIZE_BYTES,
} from "./reference-image-limits";
import { ReferenceImageThumbnail } from "./reference-image-thumbnail";

import type { ReferenceImage } from "./ai-tab";
import type { ChangeEvent, FC } from "react";

interface ReferenceImagesInputProps {
  referenceImages: ReferenceImage[];
  onReferenceImagesChange: (images: ReferenceImage[]) => void;
  uploadingReferenceImage: boolean;
  onUploadingChange: (uploading: boolean) => void;
}

export const ReferenceImagesInput: FC<ReferenceImagesInputProps> = ({
  referenceImages,
  onReferenceImagesChange,
  uploadingReferenceImage,
  onUploadingChange,
}) => {
  const getToken = useToken();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isReferenceImageLimitReached = referenceImages.length >= MAX_REFERENCE_IMAGES;

  const handleAddReferenceImage = (): void => {
    if (isReferenceImageLimitReached) return;
    fileInputRef.current?.click();
  };

  const handleFileSelect = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset input value so the same file can be selected again
      e.target.value = "";

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error("Please select a JPG or PNG image");
        return;
      }

      if (file.size > MAX_REFERENCE_IMAGE_SIZE_BYTES) {
        toast.error("Image must be less than 5MB");
        return;
      }

      onUploadingChange(true);

      try {
        const name = uuidv4();
        const imagesService = new ImagesService();
        const result = await imagesService.upload(file, name, await getToken(), file.type);

        if (!result.success || !result.url) {
          toast.error("Something went wrong, please try again");
          return;
        }

        onReferenceImagesChange([...referenceImages, { url: result.url, content_type: file.type }]);
      } catch (error) {
        console.error("Failed to upload reference image:", error);
        toast.error("Something went wrong, please try again");
      } finally {
        onUploadingChange(false);
      }
    },
    [getToken, referenceImages, onReferenceImagesChange, onUploadingChange],
  );

  const handleRemoveReferenceImage = useCallback(
    (urlToRemove: string) => {
      onReferenceImagesChange(referenceImages.filter((img) => img.url !== urlToRemove));
    },
    [referenceImages, onReferenceImagesChange],
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        {(referenceImages.length > 0 || uploadingReferenceImage) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {referenceImages.map((img) => (
              <ReferenceImageThumbnail
                key={img.url}
                url={img.url}
                onRemove={handleRemoveReferenceImage}
              />
            ))}
            {uploadingReferenceImage && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                  border: 1,
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        )}
        <Tooltip
          title={isReferenceImageLimitReached ? "Three reference image limit reached" : ""}
          placement="bottom"
          arrow
        >
          <Box
            onClick={handleAddReferenceImage}
            data-analytics-id="sidebar-ai-add-reference-image"
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: isReferenceImageLimitReached ? "default" : "pointer",
              color: isReferenceImageLimitReached ? "text.disabled" : "primary.main",
              "&:hover": {
                textDecoration: isReferenceImageLimitReached ? "none" : "underline",
              },
            }}
          >
            <AddCircleOutlined sx={{ fontSize: 18, mr: 0.5 }} />
            <Typography
              variant="body2"
              color={isReferenceImageLimitReached ? "text.disabled" : "primary.main"}
            >
              Add a reference image
            </Typography>
          </Box>
        </Tooltip>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={(e) => void handleFileSelect(e)}
      />
    </>
  );
};
